import React, { useMemo } from 'react';
import { useScopedDictText } from '../../../utils/dictOptions';
import { fieldDisplayName } from '../../../api/schemaField';
import { useSchemaFieldNames } from '../../../utils/useSchemaFieldNames';
import { FieldIssue } from '../../../components/wizard/WizardShell';
import {
    BundleParamItem,
    MaterialBundlePayload,
    MaterialBundlePreflight,
} from '../../../api/bundle';
import { RESULT_TAGS } from '../../../api/parameter';
import { IssueByPath } from './useBundleDraft';
import CopyParamsPanel from './CopyParamsPanel';

// 向导第 ④ 步: 加工参数 (可选).
//
// **只能从已有的已发布参数里选, 不能在向导里造新参数** (2026-09-17 口径).
//
// 两个理由:
//   产品上 —— 新材料该挂的是别处已经验证过的参数组合, 而不是建材料时顺手编出来的数值;
//   真要录新参数值, 去「工艺参数」页走它自己的流程 (草稿 → 送审 → 发布), 那里才有
//   完整的模板校验、效果图、矩阵这些配套。
//   权限上 —— 这一步因此只依赖 param_row:get: 选行读参数行, 落库走 material_bundle:create
//   (随套件一个事务)。不需要设备工况与参数模板的读权, 也就不必为了建材料把设备侧、
//   工艺参数侧的数据面塞进「耗材业务」角色。
//
// 所以这里不出现设备清单、模板解析、xlsx 导入 —— 它们都是"造参数"才需要的东西。

interface Props {
    payload: MaterialBundlePayload;
    setPayload: (p: MaterialBundlePayload) => void;
    preflight: MaterialBundlePreflight | null;
    issueByPath: IssueByPath;
}

/** SUMMARY_MAX 列表里最多展示几项参数值, 其余折成计数. */
const SUMMARY_MAX = 3;

/**
 * ParamsSummary 参数值在列表里的摘要: 一行参数十几个字段, 全平铺出来会把表格撑成一堵字墙,
 * 挑行的时候反而看不清哪行是哪行. 这里只给前几项 (中文名), 其余折成计数.
 * 布尔/空值不进摘要 —— 摘要要回答的是"这行大概什么档位", false 和 0 帮不上忙.
 */
function ParamsSummary({
    params,
    fieldNames,
}: {
    params?: Record<string, unknown>;
    fieldNames: Map<string, string>;
}) {
    const entries = Object.entries(params || {}).filter(
        ([, v]) => v !== '' && v !== null && v !== undefined && v !== false,
    );
    if (entries.length === 0) return <span className="text-slate-300">—</span>;
    const head = entries.slice(0, SUMMARY_MAX);
    const rest = entries.length - head.length;
    return (
        <span className="text-slate-600">
            {head.map(([k, v], i) => (
                <span key={k}>
                    {i > 0 && <span className="text-slate-300"> · </span>}
                    <span className="text-slate-400">{fieldDisplayName(k, fieldNames)}</span>{' '}
                    <span className="font-mono">{String(v)}</span>
                </span>
            ))}
            {rest > 0 && <span className="text-slate-300"> 等 {entries.length} 项</span>}
        </span>
    );
}

function StepParams({ payload, setPayload, preflight, issueByPath }: Props) {
    const rows = payload.params || [];
    const setRows = (next: BundleParamItem[]) => setPayload({ ...payload, params: next });
    const fieldNames = useSchemaFieldNames();
    // 加工类型是按框架分 scope 的字典, 列表里可能混着多个框架的行
    const processText = useScopedDictText(
        'process_type',
        rows.map((r) => r.archCode),
    );
    const archLabel = useMemo(() => {
        const m: Record<string, string> = {};
        (preflight?.archs || []).forEach((a) => {
            m[a.code] = a.label;
        });
        return m;
    }, [preflight]);

    return (
        <>
            <div className="infobox text-xs">
                加工参数只能从已有的<b>已发布</b>参数里选 ——
                新材料挂的应该是别处已经验证过的参数组合。 要录全新的参数值,
                请在材料创建完成后去「工艺参数」页按它自己的流程提交 (草稿 → 送审 → 发布)。
            </div>

            <CopyParamsPanel
                preflight={preflight}
                onPick={(items) => setRows([...rows, ...items])}
            />

            <div className="card p-4 space-y-2">
                <div className="text-sm font-semibold">
                    本次挂到新材料的参数行 ({rows.length})
                </div>
                {rows.length === 0 ? (
                    <div className="text-xs text-slate-400">
                        还没有参数行。本步可直接跳过, 创建后随时能在参数页补挂。
                    </div>
                ) : (
                    <>
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr>
                                        <th className="th">框架 / 设备</th>
                                        <th className="th">加工类型</th>
                                        <th className="th">参数</th>
                                        <th className="th">检查结果</th>
                                        <th className="th w-14" />
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((r, i) => (
                                        <tr key={`${r.deviceKey}-${i}`}>
                                            <td className="td text-xs">
                                                <div className="text-slate-400">
                                                    {archLabel[r.archCode] || r.archCode}
                                                </div>
                                                <div className="font-mono">{r.deviceKey}</div>
                                                <FieldIssue
                                                    issue={issueByPath[`params[${i}].deviceKey`]}
                                                />
                                                <FieldIssue issue={issueByPath[`params[${i}]`]} />
                                            </td>
                                            <td className="td text-xs">
                                                {processText(r.archCode, r.subProcess)}
                                            </td>
                                            <td className="td text-xs max-w-[22rem]">
                                                <ParamsSummary
                                                    params={r.params}
                                                    fieldNames={fieldNames}
                                                />
                                            </td>
                                            <td className="td text-xs">
                                                {RESULT_TAGS.find((t) => t.v === r.resultTag)
                                                    ?.label ||
                                                    r.resultTag ||
                                                    '—'}
                                            </td>
                                            <td className="td">
                                                <button
                                                    type="button"
                                                    className="btn-danger-text"
                                                    onClick={() =>
                                                        setRows(rows.filter((_, j) => j !== i))
                                                    }
                                                >
                                                    移除
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="text-[11px] text-slate-400">
                            参数值按原样挂过来, 向导里不提供编辑 —— 要调数值请在材料创建后去参数页改。
                        </div>
                    </>
                )}
            </div>
        </>
    );
}

export default StepParams;
